import {
  Badge,
  Divider,
  Flex,
  IconButton,
  SkeletonText,
  SynchronizeIcon,
  Tooltip
} from '@libs/ui/ds'
import { usePlanningTranslation } from '@libs/utils/translations'
import { useMemoSelector } from '@spa/redux/hooks'
import { entitiesActions } from '@spa/scenes/Plannings/redux/modules/entities'
import { COUNTERS_MODE } from '@spa/scenes/Plannings/redux/reducers/settings/settings'
import {
  contractCountersSelectors,
  contractsSelectors,
  getDisplayFilterByName,
  getParams,
  getSettings,
  usePlanningPolicies
} from '@spa/scenes/Plannings/redux/selectors'
import { isEqual, isNumber } from 'lodash-es'
import React from 'react'
import type { FC } from 'react'
import { useDispatch } from 'react-redux'
import { useRoleRights } from 'snap-redux/modules/memberships/utils'
import { isSocketEnabled } from 'snap-redux/modules/socket/selectors'

import BadgeCounter from './BadgeCounter'
import { ModulationCounters } from './ModulationCounters/ModulationCounters'

export type Props = {
  contractId: number
}
const EmployeeCounters: FC<Props> = ({ contractId }) => {
  const dispatch = useDispatch()
  const { t, tDates } = usePlanningTranslation()
  const { countersMode } = useMemoSelector(getSettings)

  const { atLeast } = useRoleRights()
  const policies = usePlanningPolicies()
  const socketEnabled = useMemoSelector(isSocketEnabled)
  const { date } = useMemoSelector(getParams)
  const showCTCounter = useMemoSelector(state =>
    getDisplayFilterByName(state, { name: 'showCTCounter' })
  )
  const userContractCounter =
    contractCountersSelectors.useGetCachedById(contractId)

  const userContract = contractsSelectors.useGetCachedById(contractId)

  const onRefreshCounter = () => {
    dispatch(
      entitiesActions.fetchAndHydrateContractCounters({
        contractIds: [contractId],
        // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
        date,
        params: { live: true }
      })
    )
  }

  if (!userContractCounter) {
    return <SkeletonText size='xs' fillWidth />
  }

  const counters = userContractCounter.type_counter

  return (
    <div className='text-xxs text-neutral-800'>
      <Flex row className='items-center'>
        {/* Contract / Real hours / Real absences / Gap */}
        {countersMode === COUNTERS_MODE.realized && (
          <>
            <Tooltip
              id='contract-hours'
              label={t('weekly.team.row.employeeCounters.contractsHours')}
            >
              <span data-testid={`realized-contract-hours-${contractId}`}>
                {tDates('duration.hourDecimalToHours', {
                  duration: counters.realized.contract_hours
                })}
              </span>
            </Tooltip>
            <Divider vertical className='mx-1 !h-3 border-neutral-500' />
            <Tooltip
              id='current-week-real-hours'
              label={t('weekly.team.row.employeeCounters.realWeekHours')}
            >
              <span data-testid={`realized-real-hours-${contractId}`}>
                {tDates('duration.hourDecimalToHours', {
                  duration: counters.realized.real_hours
                })}
              </span>
            </Tooltip>
            <Divider vertical className='mx-1 !h-3 border-neutral-500' />
            <Tooltip
              id='realized_rest_hours'
              label={t('weekly.team.row.employeeCounters.takenRests')}
            >
              <span data-testid={`realized-rest-hours-${contractId}`}>
                {tDates('duration.hourDecimalToHours', {
                  duration: counters.realized.rest_hours
                })}
              </span>
            </Tooltip>

            <Divider vertical className='mx-1 !h-3 border-neutral-500' />
            <Tooltip
              id='realized_delta_hours'
              label={t('weekly.team.row.employeeCounters.gap')}
            >
              <BadgeCounter
                count={counters.realized.delta_hours}
                label={tDates('duration.hourDecimalToHours', {
                  duration: counters.realized.delta_hours
                })}
                data-testid={`realized-delta-hours-${contractId}`}
              />
            </Tooltip>
          </>
        )}

        {/* Real hours / monthly cumulative gap */}
        {countersMode === COUNTERS_MODE.monthly && (
          <>
            <Tooltip
              id='current-week-real-hours'
              label={t('weekly.team.row.employeeCounters.realMonthHours')}
            >
              <span data-testid={`sum-real-worked-hours-${contractId}`}>
                {tDates('duration.hourDecimalToHours', {
                  duration: counters.sum.real_worked_hours
                })}
              </span>
            </Tooltip>

            {policies.canSeeMonthlyGapCounters &&
              isNumber(userContract.contract_time) && (
                <>
                  <Divider vertical className='mx-1 !h-3 border-neutral-500' />
                  <Tooltip
                    id='monthly-gap'
                    label={t(
                      'weekly.team.row.employeeCounters.amountMontlyGap'
                    )}
                  >
                    <BadgeCounter
                      count={counters.sum.delta_hours}
                      label={tDates('duration.hourDecimalToHours', {
                        duration: counters.sum.delta_hours
                      })}
                      data-testid={`monthly-gap-${contractId}`}
                    />
                  </Tooltip>
                </>
              )}
          </>
        )}

        {/* Contract / Planned hours / Planned absences / gap */}
        {countersMode === COUNTERS_MODE.planned && (
          <>
            <Tooltip
              id='contract-hours'
              label={t('weekly.team.row.employeeCounters.contractsHours')}
            >
              <span data-testid={`planned-contract-hours-${contractId}`}>
                {tDates('duration.hourDecimalToHours', {
                  duration: counters.planned.contract_hours
                })}
              </span>
            </Tooltip>
            <Divider vertical className='mx-1 !h-3 border-neutral-500' />
            <Tooltip
              id='current-week-planned-hours'
              label={t('weekly.team.row.employeeCounters.plannedWeekHours')}
            >
              <span data-testid={`planned-week-hours-${contractId}`}>
                {tDates('duration.hourDecimalToHours', {
                  duration: counters.planned.planned_week_hours
                })}
              </span>
            </Tooltip>
            <Divider vertical className='mx-1 !h-3 border-neutral-500' />
            <Tooltip
              id='planned-abscences'
              label={t('weekly.team.row.employeeCounters.plannedRests')}
            >
              <span data-testid={`planned-rest-hours-${contractId}`}>
                {tDates('duration.hourDecimalToHours', {
                  duration: counters.planned.rest_hours
                })}
              </span>
            </Tooltip>
            <Divider vertical className='mx-1 !h-3 border-neutral-500' />
            <Tooltip id='gap' label={t('weekly.team.row.employeeCounters.gap')}>
              <BadgeCounter
                count={counters.planned.delta_hours}
                label={tDates('duration.hourDecimalToHours', {
                  duration: counters.planned.delta_hours
                })}
                data-testid={`planned-delta-hours-${contractId}`}
              />
            </Tooltip>
          </>
        )}

        {counters.modulation && (
          <>
            <Divider vertical className='mx-1 !h-3 border-neutral-500' />
            <ModulationCounters
              modulationCounters={counters.modulation}
              contractId={contractId}
            />
          </>
        )}
        {policies.canSeeRCRCounters &&
          showCTCounter &&
          counters.compensatory_rest_counter && (
            <>
              <Divider vertical className='mx-1 !h-3 border-neutral-500' />
              <Badge
                size='s'
                variant='light'
                color='secondary.green'
                label={t('weekly.rcrCounter', {
                  duration:
                    counters.compensatory_rest_counter.balance_in_minutes
                })}
                data-testid={`rcr-balance-${contractId}`}
              />
            </>
          )}
        {(!socketEnabled ||
          (atLeast.manager && countersMode === COUNTERS_MODE.monthly)) && (
          <>
            <Divider vertical className='mx-1 !h-3 border-neutral-500' />
            <IconButton
              variant='secondary'
              intent='neutral-dark'
              icon={<SynchronizeIcon />}
              size='xs'
              onClick={onRefreshCounter}
            />
          </>
        )}
      </Flex>
    </div>
  )
}

export default React.memo(EmployeeCounters, isEqual)
